import { PrimaryButton } from '@/components/buttons';
import { Loading } from '@/components/loading';
import { Body2, H2 } from '@/components/typography';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { routes } from '@/modules/Router';
import { useValidateResetToken } from '@/modules/Users/ResetToken';
import { useFetchCountryCodes, useOtpSetup } from '@/modules/Users/Users.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { OtpSetupInputSchema, otpSetupInputSchema, ResetType } from './Users.schema';

export function OtpSetupForm() {
  const { register, handleSubmit, formState, setValue } = useForm<OtpSetupInputSchema>({
    mode: 'onSubmit',
    resolver: zodResolver(otpSetupInputSchema),
    defaultValues: {
      country_code: 'GB',
      phone_number: '',
    },
  });

  const { data: countryCodes } = useFetchCountryCodes();
  const mutation = useOtpSetup();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const reset_type = ResetType.OTP;
  const validToken = useValidateResetToken({ token, reset_type });

  if (validToken === false) {
    throw new Error('Invalid Token');
  }

  const onSubmit = (data: OtpSetupInputSchema) => {
    mutation.mutate(
      {
        phone_number: data.phone_number,
        country_code: (data.country_code || 'GB').toUpperCase(),
        token,
        reset_type,
      },
      {
        onSuccess: () => {
          toast.success('Passcode device has been set up! You can now log in.', { autoClose: 5000 });
          navigate(routes.login);
        },
        onError: (error) => {
          toast.error(error.message, { autoClose: 3000 });
          console.error('Could not set up passcode device. Error message:', error);
        },
      }
    );
  };

  return (
    <div className={cn('h-full flex items-center mt-10')}>
      <form onSubmit={handleSubmit(onSubmit)} className={cn('flex flex-col gap-6 w-[640px] shadow rounded-lg p-20')}>
        <H2 className={cn('text-center')}>Passcode Device Setup</H2>
        <Body2 className={cn('text-center text-neutral-500')}>
          When logging in, you will enter a passcode sent to you via SMS. Please enter a mobile phone number below.
        </Body2>

        {/* Country selector (dropdown) */}
        <div>
          <label htmlFor="country_code" className={cn('block mb-2 text-sm font-medium')}>
            Country
          </label>
          <select
            id="country_code"
            {...register('country_code')}
            onChange={(e) => setValue('country_code', e.target.value.toUpperCase(), { shouldValidate: true })}
            className={cn('w-full border rounded px-3 py-2')}
            defaultValue="GB"
          >
            {/* fallback option in case the list hasn't loaded yet */}
            <option value="GB">United Kingdom (+44)</option>
            {(countryCodes || []).map((c) => (
              <option key={c.code} value={c.code}>
                {c.name} ({c.calling_code})
              </option>
            ))}
          </select>
          {formState.errors.country_code?.message && (
            <div className={cn('h-4')}>
              <div className={cn('text-error-500 bg-error-50 border-error-200 rounded px-2 py-1 mt-0 mx-2 text-sm')}>
                {formState.errors.country_code?.message}
              </div>
            </div>
          )}
        </div>

        {/* Phone number input */}
        <div>
          <Input {...register('phone_number')} id={'phone_number'} placeholder={'+XXXXXXXXXXXX'} />
          {formState.errors.phone_number?.message && (
            <div className={cn('h-4')}>
              <div className={cn('text-error-500 bg-error-50 border-error-200 rounded px-2 py-1 mt-0 mx-2 text-sm')}>
                {formState.errors.phone_number?.message}
              </div>
            </div>
          )}
        </div>
        <p className={cn('text-sm text-neutral-500 text-center')}>
          By providing your mobile number, you agree that it will be used to send you a one-time text message to verify your account each
          time you login to Agricarbon. Message and data rates may apply. View{' '}
          <a
            href="https://www.agricarbon.co.uk/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
            className={cn('text-primary-600 underline hover:text-primary-800')}
          >
            T&Cs
          </a>{' '}
          and{' '}
          <a
            href="https://www.agricarbon.co.uk/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className={cn('text-primary-600 underline hover:text-primary-800')}
          >
            Privacy Policy
          </a>
          .
        </p>

        <div className={cn('flex gap-6')}>
          <PrimaryButton type="submit" disabled={mutation.status === 'pending'}>
            {mutation.status === 'pending' ? (
              <div className={cn('w-full flex justify-center items-center')}>
                <Loading size={'small'} inline />
              </div>
            ) : (
              'Submit Phone Number'
            )}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}
